/**
 * ContentOutlinePreviewSlot
 *
 * Renders a live-polling outline queue table for the blog post content
 * strategizer inside a chat behaviour slot. Each chat entry is started through
 * the same SOR endpoint as /ui/blogs' "Draft new blog" (generate_outline_now),
 * and the table shows the tenant's outline blogs with their live status.
 *
 * Action buttons open the outline's review/result page, or /ui/blogs,
 * keeping the slot as a lightweight preview.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { IconButton } from '@strivelabs/ui-core/v2';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';
import { z } from 'zod/v4';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { ROUTES } from '@/agents/blog-post-content-strategizer/constants';
import { buildBlogKey, outlineOptions } from '@/agents/blog-post-content-strategizer/screens/TriggerScreen';
import { ContentOutlineTable } from '@/components/content-outline/ContentOutlineTable';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { useSorBlogQueue, type SorQueueSubmitResult } from '@/hooks/useSorBlogQueue';
import { useResolveWorkflowInstanceId } from '@/hooks/useResolveWorkflowInstanceId';
import { generateOutlineNow } from '@/services/DataService';
import { toOutlineQueueRow } from '@/utils/sorBlogQueueRows';
import { validateUrl } from '@/lib/utils';
import { usePersistentEntryStore } from '@/hooks/usePersistentEntryStore';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  parseKeywords,
  getSlotActionButton,
} from './shared';

/**
 * Zod schema for Content Outline entry validation. Every field is nullish:
 * the chat tool's own schema sends `null` for anything it has no value for
 * (e.g. `content_goal: null`), which must not fail the whole entry.
 */
const ContentOutlineEntrySchema = z.object({
  primary_keywords: z.union([z.array(z.string()), z.string()]).nullish(),
  secondary_keywords: z.union([z.array(z.string()), z.string()]).nullish(),
  product_name: z.string().nullish(),
  content_focus_angle: z.string().nullish(),
  content_goal: z.string().nullish(),
  outline_type: z.string().nullish(),
  plp_url: z.string().nullish(),
});

/**
 * Starts one chat entry's outline through generate_outline_now, with the
 * same brief fields /ui/blogs' "Draft new blog" sends. The chat's
 * `content_goal` goes in as `notes_to_agent`, the brief field the
 * strategizer actually reads.
 *
 * @param entry - The raw chat entry.
 *
 * @returns Whether the outline was started.
 */
export async function startOutlineFromChatEntry (entry: unknown): Promise<SorQueueSubmitResult> {
  const parsed = ContentOutlineEntrySchema.safeParse(entry);
  if (!parsed.success) {
    console.warn('[ContentOutlinePreviewSlot] Invalid entry format:', parsed.error);
    return 'skipped';
  }
  const e = parsed.data;
  const primary = parseKeywords(e.primary_keywords);
  if (primary.length === 0) {
    return 'skipped';
  }

  const plpUrl = e.plp_url?.trim() ?? '';
  const isPlp = e.outline_type === outlineOptions.PLP_OUTLINE && plpUrl.length > 0 && validateUrl(plpUrl);
  const productName = e.product_name?.trim();
  const contentFocusAngle = e.content_focus_angle?.trim();
  const contentGoal = e.content_goal?.trim();

  const result = await generateOutlineNow(buildBlogKey(primary[0]), {
    primary_keywords: primary,
    secondary_keywords: parseKeywords(e.secondary_keywords),
    outline_type: isPlp ? outlineOptions.PLP_OUTLINE : outlineOptions.DEFAULT_OUTLINE,
    ...(isPlp ? { plp_url: plpUrl } : {}),
    ...(!isPlp && productName ? { product_name: productName } : {}),
    ...(contentFocusAngle ? { content_focus_angle: contentFocusAngle } : {}),
    ...(!isPlp && contentGoal ? { notes_to_agent: contentGoal } : {}),
  });

  return result.status === 'claimed' ? 'claimed' : 'rejected';
}

/** Where the old agent page lived; its blogs now live on /ui/blogs. */
const BLOGS_PATH = '/ui/blogs';

export interface ContentOutlinePreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function ContentOutlinePreviewSlot({
  slotId,
  action,
  chatHistoryId,
  behaviourActionState,
}: ContentOutlinePreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const agentShortName = action.agent_short_name;
  const resolvedWorkflowInstanceId = useResolveWorkflowInstanceId(agentShortName);
  const workflowInstanceId = resolvedWorkflowInstanceId ?? '';
  const entries = action.data?.entries;

  const { workflowInstanceId: ctxWorkflowInstanceId, workflowExecutionId } = useChatMetadata();

  // Use hook to manage persistent entry storage state
  const { alreadyStored, handleStoreSuccess } = usePersistentEntryStore(
    action,
    behaviourActionState,
    chatHistoryId,
    ctxWorkflowInstanceId,
    workflowExecutionId
  );

  const { blogs, isStoring, storeError, isLoading, isError } = useSorBlogQueue({
    source: 'outline',
    entries,
    actionId: action.id,
    alreadyStored: alreadyStored || Boolean(action.from_history),
    onStoreSuccess: handleStoreSuccess,
    submitEntry: startOutlineFromChatEntry,
    enabled: !!workflowInstanceId,
  });
  const data = useMemo(() => blogs.map(toOutlineQueueRow), [blogs]);

  const handleNavigateToBlogs = useCallback(() => {
    navigate(BLOGS_PATH);
  }, [navigate]);

  const renderAction = useCallback(
    (row: { _status: ProcessingStatus; data?: { workflow_execution_id?: string }; key?: string }) => {
      // Review, result and draft all live on the outline's result page, the
      // same link /ui/blogs' outline history uses.
      const opensResult = row._status === ProcessingStatus.WAITING_FOR_APPROVAL
        || row._status === ProcessingStatus.COMPLETED
        || row._status === ProcessingStatus.DRAFT_GENERATED;
      if (opensResult && row.data?.workflow_execution_id && row.key) {
        const resultUrl = `${ROUTES.result(row.data.workflow_execution_id)}?key=${encodeURIComponent(row.key)}&from=blogs`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(resultUrl),
        });
      }
      return getSlotActionButton({
        status: row._status,
        onNavigateToAgent: handleNavigateToBlogs,
      });
    },
    [handleNavigateToBlogs, navigate]
  );

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  if (!workflowInstanceId) return null;

  // -- Render helpers ---------------------------------------------------------

  const renderBodyContent = () => {
    if (isStoring) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Starting outlines...
          </p>
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      );
    }
    if (isError || data.length === 0) {
      return (
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError
              ? 'Unable to load content outline tasks.'
              : 'No outlines yet.'}
          </p>
        </div>
      );
    }
    return (
      <>
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Some outlines could not be started. Showing existing outlines.
            </p>
          </div>
        )}
        <div className={storeError ? 'px-6 pb-6' : 'p-8'}>
          <ContentOutlineTable data={data} renderAction={renderAction} />
        </div>
      </>
    );
  };

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
            >
              {renderBodyContent()}
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------

  // Store phase loading state
  if (isStoring) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Starting outlines...
          </p>
        </div>
      </SlotContainer>
    );
  }

  // Fetch phase loading state
  if (isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (isError || data.length === 0) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError ? 'Unable to load content outline tasks.' : 'No outlines yet.'}
          </p>
        </div>
      </SlotContainer>
    );
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <IconButton variant="secondary" size="2xs"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </IconButton>

        {/* Store error warning */}
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Some outlines could not be started. Showing existing outlines.
            </p>
          </div>
        )}

        {/* Table content */}
        <div className={storeError ? "px-6 pb-6" : "p-8"}>
          <ContentOutlineTable data={data} renderAction={renderAction} />
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <ContentOutlineTable data={data} renderAction={renderAction} />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
